import { NotifificationTemplate } from '../pages/NotificationListPage';
import { commonApi, OffsetLimit, StatusPagesResponse, StatusResponse } from './api';
import { AccessRequestStatus, UserRequest } from './users_v1';


export interface NotificationResponse<T> {
    id: 35,
    isnew: true,
    added: string,
    template: string, // TODO список допустимых значений
    prio: 0,
    context: T
}

export interface NotificationAccessContext {
    request: {
        id: number,
        url: string,
        sender: {
            user: string,
            email: string,
            phone: string,
            company: string,
            user_id: number,
            lastname: string,
            firstname: string
        },
        status: AccessRequestStatus,
        content: {
            id: 14,
            title: string,
            title_eng: string
        },
        lastmsg: {
            user: string,
            status: AccessRequestStatus,
            message: string,
            timestamp: string
        },
        updated: string,
        receiver: {
            user: string,
            email: string,
            phone: string,
            company: string,
            user_id: number,
            lastname: string,
            firstname: string
        }
    }
}

export interface NotificationNewUserContext extends UserRequest {
    last_updated: string,
    email_verification: string
}

export interface TgBot {
    url: string,
    qrcode: string,
    generated: string,
    expire: string,
}

export interface NotificationSystem<T> {
    id: number,
    added: string,
    template: NotifificationTemplate,
    group: {
        id: number,
        name: string
    },
    prio: number,
    context: T
}

export interface NotificationSystemTemplate {
    id: number,
    name: NotifificationTemplate,
    description: string | null,
    typs: number,
    enabled: number,
    actenabled: number,
    group: {
        id: number,
        name: string
    }
}

export interface NotificationSystemTemplateGroup {
    id: number,
    name: string,
    description: string,
    enabled: number,
    templates: {
        id: number,
        name: NotifificationTemplate,
        description: string | null,
        typs: number,
        actenabled: number
    }[]
}

export interface NotificationGroup {
    id: number,
    description: string,
    enabled: number,
}

export const notifyApi = commonApi.injectEndpoints({
    endpoints: (builder) => ({
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        getNewNotificationsUser: builder.query<StatusResponse<NotificationResponse<any>>, void>({
            query: () => 'be/api/v1/notification/poll/?clear=False',
            providesTags: () => [{ type: 'Notify' }]
        }),
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        getNotificationsUser: builder.query<StatusPagesResponse<NotificationResponse<any>>, OffsetLimit>({
            query: (params: OffsetLimit) => `be/api/v1/notification/?${Object.keys(params).filter(k => params[k]).map(k => params[k].toString().split(',').map(mk => k + '=' + mk).join('&')).join('&')}`,
            providesTags: () => [{ type: 'Notify' }],
            serializeQueryArgs: ({ queryArgs, endpointName }) => {
                const end = endpointName + JSON.stringify(Object.entries(queryArgs).filter(([k, v]) => !['offset', 'limit'].includes(k) && !!v));
                return end;
            },
            // // Always merge incoming data to the cache entry
            merge: (currentCache, newItems, otherArgs) => {
                if (otherArgs.arg.offset === 0) {
                    currentCache.results.data = newItems.results.data;
                } else {
                    currentCache.results.data.push(...newItems.results.data)
                }
            },
            // Refetch when the page arg changes
            forceRefetch({ currentArg, previousArg }) {
                return currentArg !== previousArg
            },
        }),
        getNewNotificationsAccess: builder.query<StatusResponse<NotificationResponse<NotificationAccessContext>>, void>({
            query: () => 'be/api/v1/notification/poll/?clear=False',
            providesTags: () => [{ type: 'Notify' }]
        }),
        getNotificationsAccess: builder.query<StatusPagesResponse<NotificationResponse<NotificationAccessContext>>, void>({
            query: () => 'be/api/v1/notification/',
            providesTags: () => [{ type: 'Notify' }]
        }),
        getNotificationTgBot: builder.query<StatusResponse<TgBot>, void>({
            query: () => 'be/api/v1/notification/telegram/link/',
        }),
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        getNotificationsSystem: builder.query<StatusPagesResponse<NotificationSystem<any>>, OffsetLimit>({
            query: (params: OffsetLimit) => `be/api/v1/notification/system/?${Object.keys(params).filter(k => params[k]).map(k => params[k].toString().split(',').map(mk => k + '=' + mk).join('&')).join('&')}`,
            providesTags: () => [{ type: 'Notify' }],
        }),
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        getNotificationSystem: builder.query<StatusResponse<NotificationSystem<any>>, number>({
            query: (id) => `be/api/v1/notification/system/${id}/`,
            providesTags: () => [{ type: 'Notify' }],
        }),
        deleteNotificationSystem: builder.mutation<void, number>({
            query: (id: number) => ({
                url: `be/api/v1/notification/system/${id}/`,
                method: 'DELETE',
            }),
            invalidatesTags: [{ type: 'Notify' }]
        }),
        getNotificationSystemTemplate: builder.query<StatusPagesResponse<NotificationSystemTemplate>, OffsetLimit>({
            query: (params) => `be/api/v1/notification/system/template/?${Object.keys(params).filter(k => params[k]).map(k => k + '=' + params[k]).join('&')}`,
        }),
        getNotificationsSystemTemplateGroup: builder.query<StatusResponse<NotificationSystemTemplateGroup[]>, void>({
            query: () => 'be/api/v1/notification/system/template/group/',
            providesTags: () => [{ type: 'NotifyGroup' }],
        }),
        getNotificationSystemTemplateGroup: builder.query<StatusResponse<NotificationSystemTemplateGroup[]>, number>({
            query: (id) => `be/api/v1/notification/system/template/group/${id}/`,
        }),
        updateNotificationSystemTemplateGroup: builder.mutation<StatusResponse<NotificationSystemTemplateGroup>, Partial<NotificationGroup>>({
            query: (data) => ({
                url: `be/api/v1/notification/system/template/group/${data.id}/`,
                method: 'PATCH',
                body: data
            }),
            invalidatesTags: [{ type: 'NotifyGroup' }]
        }),
    }),
})

export const {
    useGetNewNotificationsAccessQuery,
    useGetNewNotificationsUserQuery,
    useGetNotificationsAccessQuery,
    useGetNotificationsUserQuery,
    useLazyGetNotificationsUserQuery,
    useLazyGetNotificationTgBotQuery,
    useGetNotificationTgBotQuery,
    useGetNotificationsSystemQuery,
    useGetNotificationSystemQuery,
    useGetNotificationSystemTemplateQuery,
    useGetNotificationSystemTemplateGroupQuery,
    useLazyGetNotificationsSystemQuery,
    useLazyGetNotificationSystemQuery,
    useLazyGetNotificationSystemTemplateQuery,
    useLazyGetNotificationSystemTemplateGroupQuery,
    useDeleteNotificationSystemMutation,
    useGetNotificationsSystemTemplateGroupQuery,
    useLazyGetNotificationsSystemTemplateGroupQuery,
    useUpdateNotificationSystemTemplateGroupMutation,
} = notifyApi;
